/**
 * Sales Charts Component
 * Graphical visualization for sales data using Recharts
 */
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toMajor } from '../../lib/currency';

// Color palette for charts
const COLORS = [
  '#2563eb', // blue-600
  '#7c3aed', // violet-600
  '#db2777', // pink-600
  '#ea580c', // orange-600
  '#16a34a', // green-600
  '#0891b2', // cyan-600
  '#dc2626', // red-600
  '#ca8a04', // yellow-600
];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload?.length) return null;
  
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-foreground mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }} className="flex justify-between gap-4">
          <span>{entry.name}:</span>
          <span className="font-mono">
            {entry.name.includes('Qtd') ? entry.value : formatCurrency(entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

export function SalesCharts({ report, formatCurrency }) {
  // Process data for daily sales chart
  const dailySalesData = useMemo(() => {
    if (!report?.sales) return [];
    
    // Group sales by date
    const byDate = {};
    report.sales.forEach(sale => {
      const date = sale.created_at?.split('T')[0] || 'Unknown';
      if (!byDate[date]) {
        byDate[date] = { date, total: 0, count: 0 };
      }
      // Handle both minor and float values
      const saleTotal = sale.total_minor ? toMajor(sale.total_minor) : (sale.total || 0);
      byDate[date].total += saleTotal;
      byDate[date].count += 1;
    });
    
    return Object.values(byDate)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(item => ({
        ...item,
        date: new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        total: Number(item.total.toFixed(2)),
      }));
  }, [report]);

  // Process data for payment methods pie chart
  const paymentMethodsData = useMemo(() => {
    if (!report?.sales) return [];
    
    const byMethod = {};
    report.sales.forEach(sale => {
      sale.payments?.forEach(payment => {
        const method = payment.payment_method_name || 'Outro';
        if (!byMethod[method]) {
          byMethod[method] = { name: method, value: 0, count: 0 };
        }
        const amount = payment.amount_minor ? toMajor(payment.amount_minor) : (payment.amount || 0);
        byMethod[method].value += amount;
        byMethod[method].count += 1;
      });
    });
    
    return Object.values(byMethod)
      .sort((a, b) => b.value - a.value)
      .map(item => ({
        ...item,
        value: Number(item.value.toFixed(2)),
      }));
  }, [report]);

  // Process data for hourly sales chart
  const hourlySalesData = useMemo(() => {
    if (!report?.sales) return [];
    
    const byHour = {};
    for (let h = 0; h < 24; h++) {
      byHour[h] = { hour: `${h.toString().padStart(2, '0')}:00`, total: 0, count: 0 };
    }
    
    report.sales.forEach(sale => {
      const hour = new Date(sale.created_at).getHours();
      const saleTotal = sale.total_minor ? toMajor(sale.total_minor) : (sale.total || 0);
      byHour[hour].total += saleTotal;
      byHour[hour].count += 1;
    });
    
    return Object.values(byHour).map(item => ({
      ...item,
      total: Number(item.total.toFixed(2)),
    }));
  }, [report]);

  // Process data for top products chart
  const topProductsData = useMemo(() => {
    if (!report?.sales) return [];
    
    const byProduct = {};
    report.sales.forEach(sale => {
      sale.items?.forEach(item => {
        const name = item.name || 'Unknown';
        if (!byProduct[name]) {
          byProduct[name] = { name, quantity: 0, revenue: 0 };
        }
        byProduct[name].quantity += item.quantity || 1;
        const unitPrice = item.unit_price_minor ? toMajor(item.unit_price_minor) : (item.unit_price || 0);
        byProduct[name].revenue += unitPrice * (item.quantity || 1);
      });
    });
    
    return Object.values(byProduct)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)
      .map(item => ({
        ...item,
        revenue: Number(item.revenue.toFixed(2)),
      }));
  }, [report]);

  if (!report?.sales?.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64 text-muted-foreground">
          Sem dados para exibir gráficos
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="daily">Vendas Diárias</TabsTrigger>
          <TabsTrigger value="hourly">Por Hora</TabsTrigger>
          <TabsTrigger value="payments">Pagamentos</TabsTrigger>
          <TabsTrigger value="products">Top Produtos</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vendas por Dia</CardTitle>
              <CardDescription>Total de vendas por dia no período selecionado</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={dailySalesData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }} 
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `R$ ${v}`}
                  />
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                  <Area 
                    type="monotone" 
                    dataKey="total" 
                    name="Total" 
                    stroke="#2563eb" 
                    fill="url(#colorTotal)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hourly" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vendas por Hora</CardTitle>
              <CardDescription>Distribuição de vendas ao longo do dia</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={hourlySalesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval={1}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `R$ ${v}`}
                  />
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                  <Bar 
                    dataKey="total" 
                    name="Total" 
                    fill="#7c3aed" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Formas de Pagamento</CardTitle>
              <CardDescription>Distribuição por método de pagamento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentMethodsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {paymentMethodsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground mb-4">Detalhamento</h4>
                  {paymentMethodsData.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm">{formatCurrency(item.value)}</p>
                        <p className="text-xs text-muted-foreground">{item.count} transações</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top 10 Produtos</CardTitle>
              <CardDescription>Produtos mais vendidos por receita</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={topProductsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis 
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `R$ ${v}`}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={150}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0].payload;
                      return (
                        <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-medium">{data.name}</p>
                          <p className="text-muted-foreground">
                            Qtd: {data.quantity} | Receita: {formatCurrency(data.revenue)}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar 
                    dataKey="revenue" 
                    name="Receita" 
                    fill="#16a34a" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default SalesCharts;
